import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { useIwEditingStore } from '@/entities/interwills';
import { COLORS } from '@/shared/constants/colors';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { OwnershipType } from '@/shared/lib/sj-orm/models/asset.dto';
import { numbers } from '@/shared/utils/numbers';

import { DoughnutChart } from './charts/DoughnutChart';
import { DashboardItem } from './DashboardItem';
import { EmptyDistribution } from './EmptyDistribution';
import { DoughnutChartWrapper, Icon, Legend, TextContainer, Value } from './styled';

export const EMPTY_VALUES = {
  added: 100,
  notAdded: 0,
};

export const TotalDirectAssets = () => {
  const { t } = useTranslation('pages/pageInterwill', { keyPrefix: 'dashboard' });

  const sjDb = useSJDatabase();
  const allDirectAssets = sjDb.collections.assets?.findMany(
    (asset) => asset.ownerShipType === OwnershipType.DIRECT,
  );

  const { assets: iwAssets } = useIwEditingStore();
  const hasData = Boolean(allDirectAssets && allDirectAssets?.length);

  const chartData = useMemo(() => {
    const totalCount = allDirectAssets?.length ?? 0;
    const addedCount = iwAssets.length;

    const addedPercentage = (addedCount / totalCount) * 100;
    const notAddedPercentage = 100 - addedPercentage;

    return [
      { label: t('totalDirectChart.added'), value: hasData ? addedPercentage : EMPTY_VALUES.added },
      {
        label: t('totalDirectChart.notAdded'),
        value: hasData ? notAddedPercentage : EMPTY_VALUES.notAdded,
      },
    ];
  }, [allDirectAssets?.length, iwAssets.length, hasData, t]);

  if (!hasData) {
    return (
      <EmptyDistribution
        small
        icon="chart-bar"
        title={t('emptyDirect.title')}
        text={<span>{t('emptyDirect.text')}</span>}
      />
    );
  }

  return (
    <DashboardItem hasData={true}>
      <DoughnutChartWrapper>
        <DoughnutChart
          title={t('totalDirectChart.title')}
          labels={chartData.map((slice) => slice.label)}
          data={chartData.map((slice) => slice.value)}
          colors={COLORS.colorDirectAssetsCharts}
          valueType="percent"
        />
      </DoughnutChartWrapper>
      <Legend>
        <p>{t('totalDirectChart.title')}</p>
        {chartData.map(({ label, value }, index) => (
          <TextContainer key={label}>
            <Icon $bgColor={COLORS.colorDirectAssetsCharts[index]} />
            <p>{label}:</p>
            <Value>{numbers.formatPercent({ value, withSign: true, fractionDigits: 0 })}</Value>
          </TextContainer>
        ))}
      </Legend>
    </DashboardItem>
  );
};
